import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { discoveryCategories } from './site/navConfig';

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const companyLinks = [
    { label: t('nav.home', 'Home'), to: '/' },
    { label: t('nav.aboutUs', 'About Us'), to: '/about' },
    { label: t('siteNav.forProviders', 'For Providers'), to: '/pricing' },
    { label: t('common.search', 'Search'), to: '/search' },
  ];

  return (
    <footer
      data-testid="footer-section"
      className="relative overflow-hidden bg-gray-950 text-white mt-auto grain"
    >
      <span className="aurora-blob w-[24rem] h-[24rem] -top-40 start-1/4 bg-blue-600/25 animate-aurora-slow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="text-start">
            <div className="flex items-center gap-3" style={{ direction: 'ltr' }}>
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 shadow-lg shadow-blue-600/25">
                <Heart className="w-5 h-5 text-white" strokeWidth={2.4} />
              </span>
              <span>
                <span className="block font-display text-lg font-extrabold leading-none">EnayaHub</span>
                <span className="block text-xs text-white/50 mt-1">عناية هاب</span>
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-xs">
              {t('footer.description', 'Your trusted platform for booking healthcare and beauty appointments in Kuwait')}
            </p>
          </div>

          {/* Discover */}
          <div className="text-start">
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-white/40">
              {t('siteNav.discover', 'Discover')}
            </h4>
            <ul className="mt-5 space-y-3">
              {discoveryCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    onClick={() => navigate(cat.to)}
                    data-testid={`footer-link-${cat.id}`}
                    className="group inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {t(cat.labelKey, cat.fallback)}
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="text-start">
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-white/40">
              {t('footer.quickLinks', 'Quick Links')}
            </h4>
            <ul className="mt-5 space-y-3">
              {companyLinks.map((link) => (
                <li key={link.to}>
                  <button
                    onClick={() => navigate(link.to)}
                    className="text-sm text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-start">
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-white/40">
              {t('footer.contactUs', 'Contact Us')}
            </h4>
            <ul className="mt-5 space-y-3.5">
              <li>
                <a
                  href="mailto:support@enayahub.net"
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2.5 text-sm text-white/60 hover:text-white transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  support@enayahub.net
                </a>
              </li>
              <li>
                <a
                  href="tel:+96565527758"
                  data-testid="footer-phone-link"
                  className="inline-flex items-center gap-2.5 text-sm text-white/60 hover:text-white transition-colors duration-300"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span className="numeric-ltr">+965 6552 7758</span>
                </a>
              </li>
              <li className="inline-flex items-center gap-2.5 text-sm text-white/60">
                <MapPin className="w-4 h-4 text-teal-400" />
                {t('footer.address', 'Kuwait City, State of Kuwait')}
              </li>
            </ul>
            <div className="mt-6" data-testid="footer-language-switch">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © <span className="numeric-ltr">2026</span> EnayaHub (عناية هاب). {t('footer.copyright', 'All rights reserved')}
          </p>
          <button
            onClick={() => navigate('/terms')}
            data-testid="footer-terms-link"
            className="text-xs text-white/40 hover:text-white transition-colors duration-300"
          >
            {t('footer.termsConditions', 'Terms & Conditions')}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
